import React, { useState, useEffect } from 'react';
import { projectService } from '../services/projectService';
import { translationService } from '../services/translationService';
import { getLocalizedContent } from '../utils/multilingual';
import {
  Edit,
  X,
  Trash2,
  Plus,
  Image,
  Globe,
  Camera,
  Upload,
  GripVertical,
  AlertCircle,
  Loader2,
  Languages,
  Minus
} from 'lucide-react';
import './ProjectManager.css';

const ProjectManager = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState('list'); // 'list', 'add', 'edit'
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [translatingProjects, setTranslatingProjects] = useState(new Set());
  const [currentLanguage, setCurrentLanguage] = useState('SR'); // SR, EN, DE

  // Form state - now multilingual
  const [formData, setFormData] = useState({
    title: { sr: '', en: '', de: '' },
    description: { sr: '', en: '', de: '' },
    client: { sr: '', en: '', de: '' },
    location: { sr: '', en: '', de: '' },
    completionDate: '',
    category: { sr: '', en: '', de: '' },
    tags: { sr: [], en: [], de: [] },
    featured: false
  });

  const [images, setImages] = useState([]);
  const [currentTag, setCurrentTag] = useState('');

  // Drag and drop state
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [isReordering, setIsReordering] = useState(false);

  // Categories dropdown options
  const categoryOptions = [
    'Stambeni',
    'Komercijalni', 
    'Industrijski',
    'Javni',
    'Hotelski',
    'Uredski',
    'Sportski',
    'Zdravstveni'
  ];

  useEffect(() => {
    if (activeTab === 'list') {
      fetchProjects();
    }
  }, [activeTab]);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const response = await projectService.getProjects({ limit: 50 });
      setProjects(response.data.projects || []);
    } catch (err) {
      setError('Greška pri učitavanju projekata');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // New multilingual input handler
  const handleMultilingualInputChange = (field, value, language = currentLanguage) => {
    const langCode = language.toLowerCase();
    
    setFormData(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        [langCode]: value
      }
    }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);

    // Limit to 25 images
    if (files.length > 25) {
      setError('Možete upload-ovati maksimalno 25 slika odjednom');
      e.target.value = ''; // Reset file input
      return;
    }

    // Check file sizes (5MB per image)
    const maxSize = 5 * 1024 * 1024; // 5MB
    const oversizedFiles = files.filter(file => file.size > maxSize);

    if (oversizedFiles.length > 0) {
      setError(`Sledeće slike su prevelike (max 5MB): ${oversizedFiles.map(f => f.name).join(', ')}`);
      e.target.value = ''; // Reset file input
      return;
    }

    // Check total existing + new images doesn't exceed 25
    const existingCount = editingProject?.gallery?.length || 0;
    if (existingCount + files.length > 25) {
      setError(`Ukupno možete imati maksimalno 25 slika. Trenutno imate ${existingCount}, pokušavate dodati ${files.length}.`);
      e.target.value = ''; // Reset file input
      return;
    }

    setImages(files);
    setError(null); // Clear any previous errors
  };

  const addTag = () => {
    const langCode = currentLanguage.toLowerCase();
    const currentTags = formData.tags[langCode] || [];
    
    if (currentTag.trim() && !currentTags.includes(currentTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: {
          ...prev.tags,
          [langCode]: [...currentTags, currentTag.trim()]
        }
      }));
      setCurrentTag('');
    }
  };

  const removeTag = (tagToRemove) => {
    const langCode = currentLanguage.toLowerCase();
    const currentTags = formData.tags[langCode] || [];
    
    setFormData(prev => ({
      ...prev,
      tags: {
        ...prev.tags,
        [langCode]: currentTags.filter(tag => tag !== tagToRemove)
      }
    }));
  };

  const handleTagKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  };

  const validateForm = () => {
    // All fields are now optional - no validation required
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const submitData = new FormData();
      
      // Add form data - now multilingual
      submitData.append('title', JSON.stringify(formData.title));
      submitData.append('description', JSON.stringify(formData.description));
      submitData.append('client', JSON.stringify(formData.client));
      submitData.append('location', JSON.stringify(formData.location));
      submitData.append('completionDate', formData.completionDate);
      submitData.append('category', JSON.stringify(formData.category));
      submitData.append('tags', JSON.stringify(formData.tags));
      submitData.append('featured', formData.featured);

      // Add images
      images.forEach((image) => {
        submitData.append('images', image);
      });

      const token = localStorage.getItem('adminToken');
      
      if (editingProject) {
        await projectService.updateProject(editingProject._id, submitData, token);
      } else {
        await projectService.createProject(submitData, token);
      }

      // Reset form
      resetForm();
      setActiveTab('list');
      fetchProjects();
      
    } catch (err) {
      setError(err.message || 'Greška pri snimavanju projekta');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: { sr: '', en: '', de: '' },
      description: { sr: '', en: '', de: '' },
      client: { sr: '', en: '', de: '' },
      location: { sr: '', en: '', de: '' },
      completionDate: '',
      category: { sr: '', en: '', de: '' },
      tags: { sr: [], en: [], de: [] },
      featured: false
    });
    setImages([]);
    setEditingProject(null);
    setCurrentLanguage('SR');
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    
    // Helper function to ensure multilingual structure
    const ensureMultilingual = (field, fallback = '') => {
      if (typeof field === 'string') {
        return { sr: field, en: fallback, de: fallback };
      }
      return field || { sr: fallback, en: fallback, de: fallback };
    };
    
    const ensureMultilingualArray = (field) => {
      if (Array.isArray(field)) {
        return { sr: field, en: [], de: [] };
      }
      return field || { sr: [], en: [], de: [] };
    };
    
    setFormData({
      title: ensureMultilingual(project.title),
      description: ensureMultilingual(project.description),
      client: ensureMultilingual(project.client),
      location: ensureMultilingual(project.location),
      completionDate: project.completionDate ? new Date(project.completionDate).toISOString().split('T')[0] : '',
      category: ensureMultilingual(project.category),
      tags: ensureMultilingualArray(project.tags),
      featured: project.featured || false
    });
    setActiveTab('add');
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm('Da li ste sigurni da želite da obrišete ovaj projekat?')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      await projectService.deleteProject(projectId, token);
      fetchProjects();
    } catch (err) {
      setError('Greška pri brisanju projekta');
    }
  };

  const handleTranslateProject = async (projectId) => {
    if (!window.confirm('Da li ste sigurni da želite da prevedete ovaj projekat na engleski i nemački jezik pomoću AI?')) {
      return;
    }

    setTranslatingProjects(prev => new Set(prev.add(projectId)));
    
    try {
      const token = localStorage.getItem('adminToken');
      await translationService.translateProject(projectId, ['en', 'de'], token);
      setError(null);
      alert('Projekat je uspešno preveden!');
      fetchProjects(); // Refresh the list
    } catch (err) {
      console.error('Translation error:', err);
      setError('Greška pri prevođenju projekta: ' + err.message);
    } finally {
      setTranslatingProjects(prev => {
        const newSet = new Set(prev);
        newSet.delete(projectId);
        return newSet;
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('sr-RS');
  };

  // Drag and drop handlers for gallery images with better error handling
  const handleDragStart = (e, index) => {
    if (!editingProject?.gallery || index >= editingProject.gallery.length) {
      e.preventDefault();
      return;
    }

    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
    setDraggedIndex(index);

    // Add visual feedback to the dragged element
    setTimeout(() => {
      e.target.style.opacity = '0.5';
    }, 0);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();

    if (!editingProject?.gallery) {
      setDraggedIndex(null);
      return;
    }

    const draggedIndexFromData = parseInt(e.dataTransfer.getData('text/plain'));

    if (draggedIndexFromData === dropIndex || draggedIndex === null) {
      setDraggedIndex(null);
      return;
    }

    // Validate indices
    if (draggedIndexFromData < 0 || draggedIndexFromData >= editingProject.gallery.length ||
        dropIndex < 0 || dropIndex >= editingProject.gallery.length) {
      setDraggedIndex(null);
      return;
    }

    try {
      setIsReordering(true);

      // Create new gallery array with reordered images
      const newGallery = [...editingProject.gallery];
      const draggedImage = newGallery[draggedIndexFromData];

      // Validate that the dragged image exists
      if (!draggedImage) {
        throw new Error('Invalid image index');
      }

      // Remove dragged image
      newGallery.splice(draggedIndexFromData, 1);

      // Insert at new position
      newGallery.splice(dropIndex, 0, draggedImage);

      // Update local state immediately for visual feedback
      setEditingProject(prev => ({
        ...prev,
        gallery: newGallery
      }));

      // Save new order to backend
      const token = localStorage.getItem('adminToken');
      const response = await projectService.reorderGalleryImages(
        editingProject._id,
        newGallery.map((img, idx) => ({
          imageUrl: img.url,
          newPosition: idx,
          alt: img.alt || `Project image ${idx + 1}`
        })),
        token
      );

      // Update with response from server to ensure consistency
      if (response.success) {
        setEditingProject(response.data);
        console.log('Image order updated successfully');
      }

    } catch (err) {
      console.error('Reorder error:', err);
      setError('Greška pri ažuriranju redosleda slika: ' + err.message);
      // Revert local changes on error by refetching
      try {
        const token = localStorage.getItem('adminToken');
        const response = await projectService.getProjectById(editingProject._id);
        setEditingProject(response.data);
      } catch (fetchErr) {
        console.error('Error reverting changes:', fetchErr);
      }
    } finally {
      setIsReordering(false);
      setDraggedIndex(null);
    }
  };

  const handleDragEnd = (e) => {
    // Reset visual feedback
    e.target.style.opacity = '1';
    setDraggedIndex(null);
  };

  const handleDeleteImage = async (imageIndex) => {
    if (!editingProject?.gallery ||
        imageIndex < 0 ||
        imageIndex >= editingProject.gallery.length ||
        !window.confirm('Da li ste sigurni da želite da obrišete ovu sliku?')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await projectService.deleteImage(editingProject._id, imageIndex, token);

      if (response.success) {
        // Update local state with response from server
        setEditingProject(response.data);
        console.log('Image deleted successfully');
      }

    } catch (err) {
      console.error('Delete error:', err);
      setError('Greška pri brisanju slike: ' + err.message);
    }
  };

  return (
    <div className="project-manager">
      <div className="project-manager-content">
        <div className="project-manager-header">
          <h2>Upravljanje projektima</h2>
          <button className="close-btn" onClick={onClose} title="Zatvori">
            <X size={24} />
          </button>
        </div>

        <div className="project-manager-tabs">
          <button 
            className={`tab-btn ${activeTab === 'list' ? 'active' : ''}`}
            onClick={() => { setActiveTab('list'); resetForm(); }}
          >
            Lista projekata
          </button>
          <button 
            className={`tab-btn ${activeTab === 'add' ? 'active' : ''}`}
            onClick={() => { setActiveTab('add'); resetForm(); }}
          >
            {editingProject ? 'Uredi projekat' : 'Dodaj novi projekat'}
          </button>
        </div>

        {error && (
          <div className="error-message">
            <AlertCircle size={20} />
            {error}
          </div>
        )}

        {activeTab === 'list' && (
          <div className="projects-list">
            {loading ? (
              <div className="loading-state">Učitavanje...</div>
            ) : (
              <div className="projects-grid">
                {projects.map(project => (
                  <div key={project._id} className="project-item">
                    <div className="project-image">
                      <img 
                        src={project.gallery?.[0]?.url || '/images/placeholder/project-placeholder.jpg'} 
                        alt={getLocalizedContent(project.title, 'SR')}
                      />
                      {project.featured && (
                        <div className="featured-badge">
                          <svg viewBox="0 0 24 24" fill="none">
                            <path
                              d="M12 2L15.09 8.26L22 9L17 14L18.18 21L12 17.77L5.82 21L7 14L2 9L8.91 8.26L12 2Z"
                              fill="currentColor"
                            />
                          </svg>
                        </div>
                      )}
                    </div>
                    <div className="project-info">
                      <h4>{getLocalizedContent(project.title, 'SR')}</h4>
                      <p className="project-category">{getLocalizedContent(project.category, 'SR')}</p>
                      {project.client && <p className="project-client">Klijent: {getLocalizedContent(project.client, 'SR')}</p>}
                      {project.location && <p className="project-location">Lokacija: {getLocalizedContent(project.location, 'SR')}</p>}
                      <p className="project-date">Završeno: {formatDate(project.completionDate)}</p>
                    </div>
                    <div className="project-actions">
                      <button onClick={() => handleEdit(project)} className="edit-btn">
                        Uredi
                      </button>
                      
                      <button 
                        onClick={() => handleTranslateProject(project._id)} 
                        className="translate-btn"
                        disabled={translatingProjects.has(project._id)}
                        title="Prevedi na drugi jezici pomoću AI"
                      >
                        {translatingProjects.has(project._id) ? (
                          <>
                            <Loader2 size={16} className="spinning" />
                            Prevodi...
                          </>
                        ) : (
                          <>
                            <Languages size={16} />
                            AI prevod
                          </>
                        )}
                      </button>
                      
                      <button onClick={() => handleDelete(project._id)} className="delete-btn">
                        Obriši
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'add' && (
          <form className="project-form" onSubmit={handleSubmit}>
            {/* Language Switcher */}
            <div className="language-switcher">
              <div className="language-switcher-header">
                <h4>Jezik editovanja</h4>
                <p>Izaberite jezik za unos informacija o projektu</p>
              </div>
              <div className="language-buttons">
                <button
                  type="button"
                  className={`language-btn ${currentLanguage === 'SR' ? 'active' : ''}`}
                  onClick={() => setCurrentLanguage('SR')}
                >
                  <Globe size={16} /> Srpski
                </button>
                <button
                  type="button"
                  className={`language-btn ${currentLanguage === 'EN' ? 'active' : ''}`}
                  onClick={() => setCurrentLanguage('EN')}
                >
                  <Globe size={16} /> English
                </button>
                <button
                  type="button"
                  className={`language-btn ${currentLanguage === 'DE' ? 'active' : ''}`}
                  onClick={() => setCurrentLanguage('DE')}
                >
                  <Globe size={16} /> Deutsch
                </button>
              </div>
            </div>

            {/* Basic Info */}
            <div className="form-section">
              <h3>Osnovne informacije</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Naziv projekta</label>
                  <input
                    type="text"
                    value={formData.title[currentLanguage.toLowerCase()] || ''}
                    onChange={(e) => handleMultilingualInputChange('title', e.target.value)}
                    placeholder={`Unesite naziv na ${currentLanguage === 'SR' ? 'srpskom' : currentLanguage === 'EN' ? 'engleskom' : 'nemačkom'} jeziku`}
                  />
                </div>
                
                <div className="form-group">
                  <label>Kategorija</label>
                  <select
                    value={formData.category[currentLanguage.toLowerCase()] || ''}
                    onChange={(e) => handleMultilingualInputChange('category', e.target.value)}
                  >
                    <option value="">Izaberite kategoriju</option>
                    {categoryOptions.map((cat, index) => (
                      <option key={`category-${index}`} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Klijent</label>
                  <input
                    type="text"
                    value={formData.client[currentLanguage.toLowerCase()] || ''}
                    onChange={(e) => handleMultilingualInputChange('client', e.target.value)}
                    placeholder={`Naziv klijenta na ${currentLanguage === 'SR' ? 'srpskom' : currentLanguage === 'EN' ? 'engleskom' : 'nemačkom'} jeziku`}
                  />
                </div>
                
                <div className="form-group">
                  <label>Lokacija</label>
                  <input
                    type="text"
                    value={formData.location[currentLanguage.toLowerCase()] || ''}
                    onChange={(e) => handleMultilingualInputChange('location', e.target.value)}
                    placeholder={`Lokacija na ${currentLanguage === 'SR' ? 'srpskom' : currentLanguage === 'EN' ? 'engleskom' : 'nemačkom'} jeziku`}
                  />
                </div>
                
                <div className="form-group">
                  <label>Datum realizacije</label>
                  <input
                    type="date"
                    value={formData.completionDate}
                    onChange={(e) => handleInputChange('completionDate', e.target.value)}
                  />
                </div>
                
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.featured}
                      onChange={(e) => handleInputChange('featured', e.target.checked)}
                    />
                    Istaknut projekat
                  </label>
                </div>
              </div>
              
              <div className="form-group">
                <label>Opis projekta</label>
                <textarea
                  value={formData.description[currentLanguage.toLowerCase()] || ''}
                  onChange={(e) => handleMultilingualInputChange('description', e.target.value)}
                  placeholder={`Detaljni opis na ${currentLanguage === 'SR' ? 'srpskom' : currentLanguage === 'EN' ? 'engleskom' : 'nemačkom'} jeziku...`}
                  rows="5"
                />
              </div>
            </div>

            {/* Images */}
            <div className="form-section">
              <h3><Image size={20} /> Galerija slika</h3>
              <div className="form-subsection">
                <h4 className="form-subsection-title">Upload novih slika</h4>
                <div className="form-group">
                  <label>Izaberite slike projekta</label>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="file-input"
                  />
                  <span className="form-help">Podržani formati: JPG, PNG, WebP. Maksimalna veličina: 5MB po slici. Maksimalno 25 slika po projektu. Prva slika će biti glavna.</span>
                </div>
              </div>

              {/* Show existing images with drag & drop reordering */}
              {editingProject && editingProject.gallery && editingProject.gallery.length > 0 && (
                <div className="existing-images">
                  <h4>Postojeće slike ({editingProject.gallery.length}/25)</h4>
                  <p className="reorder-instruction">
                    Prevucite slike da promenite redosled prikazivanja u galeriji
                  </p>
                  <div className={`images-grid-sortable ${isReordering ? 'loading' : ''}`}>
                    {editingProject.gallery.map((image, index) => {
                      // Safety check for image data
                      if (!image || !image.url) {
                        return null;
                      }

                      return (
                        <div
                          key={`image-${image.url}-${index}`}
                          className={`image-item-sortable ${draggedIndex === index ? 'dragging' : ''} ${isReordering ? 'reordering' : ''}`}
                          draggable={!isReordering}
                          onDragStart={(e) => handleDragStart(e, index)}
                          onDragOver={handleDragOver}
                          onDragEnter={handleDragEnter}
                          onDrop={(e) => handleDrop(e, index)}
                          onDragEnd={handleDragEnd}
                        >
                          <div className="drag-handle">
                            <GripVertical size={20} />
                          </div>
                          <div className="image-preview">
                            <img
                              src={image.url}
                              alt={image.alt || `Project image ${index + 1}`}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                console.error('Failed to load image:', image.url);
                              }}
                            />
                            <div className="image-position">#{index + 1}</div>
                          </div>
                          <div className="image-controls">
                            <button
                              type="button"
                              className="delete-image-btn"
                              onClick={() => handleDeleteImage(index)}
                              title="Obriši sliku"
                              disabled={isReordering}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {editingProject.gallery.length >= 25 && (
                    <p className="max-images-warning">
                      ⚠️ Dostigli ste maksimalni broj slika (25). Da biste dodali nove, prvo obrišite neke postojeće.
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Tags */}
            <div className="form-section">
              <h3>Tagovi</h3>
              <div className="tag-input-group">
                <input
                  type="text"
                  placeholder="Dodaj tag"
                  value={currentTag}
                  onChange={(e) => setCurrentTag(e.target.value)}
                  onKeyPress={handleTagKeyPress}
                />
                <button type="button" onClick={addTag} className="add-btn">Dodaj</button>
              </div>
              
              <div className="added-items">
                {formData.tags[currentLanguage.toLowerCase()] && formData.tags[currentLanguage.toLowerCase()].map((tag, index) => (
                  <div key={index} className="tag-item">
                    <span>{tag}</span>
                    <button type="button" onClick={() => removeTag(tag)} className="remove-btn">×</button>
                  </div>
                ))}
              </div>
              <p className="form-help">Tagovi pomažu pri pretrazi i kategorizaciji projekata</p>
            </div>

            <div className="form-actions">
              <button type="button" onClick={() => { setActiveTab('list'); resetForm(); }} className="cancel-btn">
                Otkaži
              </button>
              <button type="submit" disabled={loading} className="submit-btn">
                {loading ? 'Snimanje...' : (editingProject ? 'Ažuriraj projekat' : 'Dodaj projekat')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ProjectManager;